import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
// import Dashboard from './pages/Dashboard'; // DISABLED - not needed
import VideoUpload from './pages/VideoUpload';
// import InfluencerRankings from './pages/InfluencerRankings'; // DISABLED - not needed
import VideoDetails from './pages/VideoDetails';
import VideoPerformanceAnalysis from './pages/VideoPerformanceAnalysis';
import VideoTracking from './pages/VideoTracking';
import Settings from './pages/Settings';
import Login from './pages/Login';
import CreatorUpload from './pages/CreatorUpload';
import CreatorPortal from './pages/CreatorPortal';
import AdminAuthCallback from './pages/AdminAuthCallback';
import { Toaster } from './components/ui/sonner';
import { BarChart3, Upload, Settings as SettingsIcon, LogOut, User, Activity } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Navigation() {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  
  // Don't show navigation on login, creator-upload, or creator portal pages
  if (location.pathname === '/login' || location.pathname === '/creator-upload' || location.pathname === '/') {
    return null;
  }
  
  const navItems = [
    // { path: '/em-admin-access/dashboard', icon: BarChart3, label: 'Dashboard' }, // DISABLED - not needed
    { path: '/em-admin-access/upload', icon: Upload, label: 'Upload Videos' },
    { path: '/em-admin-access/tracking', icon: Activity, label: 'Video Tracking' },
    { path: '/em-admin-access/settings', icon: SettingsIcon, label: 'Settings' },
  ];

  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="app-title">InfluTracker</h1>
              <p className="text-xs text-gray-500">Attribution Platform</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
            
            {user && (
              <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                <div className="flex items-center space-x-2 text-gray-700 text-sm">
                  <User className="w-4 h-4" />
                  <span>{user.name}</span>
                  {isAdmin() && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Admin</span>
                  )}
                </div>
                <button
                  onClick={logout}
                  className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <BrowserRouter>
        <AuthProvider>
          <Navigation />
          <Routes>
              {/* Public Creator Portal */}
              <Route path="/" element={<CreatorPortal />} />
              
              {/* Admin OAuth Callback - processes Google OAuth and checks @emergent.sh domain */}
              <Route path="/admin-auth-callback" element={<AdminAuthCallback />} />
              
              {/* Admin Routes - Hidden under /em-admin-access */}
              <Route path="/em-admin-access" element={<Navigate to="/em-admin-access/tracking" replace />} />
              <Route path="/em-admin-access/login" element={<Login />} />
              
              {/* DISABLED: Dashboard route */}
              {/* <Route path="/em-admin-access/dashboard" element={
                <ProtectedRoute adminOnly={true}>
                  <Dashboard />
                </ProtectedRoute>
              } /> */}
              <Route path="/em-admin-access/upload" element={
                <ProtectedRoute adminOnly={true}>
                  <VideoUpload />
                </ProtectedRoute>
              } />
              <Route path="/em-admin-access/tracking" element={
                <ProtectedRoute adminOnly={true}>
                  <VideoTracking />
                </ProtectedRoute>
              } />
              {/* DISABLED: Rankings route */}
              {/* <Route path="/em-admin-access/rankings" element={
                <ProtectedRoute adminOnly={true}>
                  <InfluencerRankings />
                </ProtectedRoute>
              } /> */}
              <Route path="/em-admin-access/video/:videoId" element={
                <ProtectedRoute adminOnly={true}>
                  <VideoDetails />
                </ProtectedRoute>
              } />
              <Route path="/em-admin-access/influencer/:influencerName/performance" element={
                <ProtectedRoute adminOnly={true}>
                  <VideoPerformanceAnalysis />
                </ProtectedRoute>
              } />
              <Route path="/em-admin-access/settings" element={
                <ProtectedRoute adminOnly={true}>
                  <Settings />
                </ProtectedRoute>
              } />
              
              {/* Legacy creator upload route */}
              <Route path="/creator-upload" element={
                <ProtectedRoute>
                  <CreatorUpload />
                </ProtectedRoute>
              } />
            </Routes>
          <Toaster position="top-right" theme="dark" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;